import React from 'react';
import { Accordion, Checkbox, Dropdown, Icon, Segment, Label } from 'semantic-ui-react';
import Select from 'react-select';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { UrgencyParams } from '../../app/models/Playground';
import { sportOptions, createSportOptions } from '../../app/common/options/sportOptions';

interface UrgencyFilterProps {
  params: UrgencyParams;
  onParamChange: (p: Partial<UrgencyParams>) => void;
}

export const UrgencyFilters = observer(({ params, onParamChange }: UrgencyFilterProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const windowOptions = [2, 4, 6, 12].map(v => ({ key: v, value: v, text: `${v}h` }));
  const radiusOptions = [1, 5, 10, 20, 30, 50].map(v => ({ key: v, value: v, text: `${v} km` }));

  // Localized sport options for the multi-select
  const localizedSportOptions = createSportOptions(t, sportOptions).map(o => ({
    value: o.value,
    label: o.label,
  }));

  // Derive currently selected options from params.sportIds
  const selectedSportOptions = localizedSportOptions.filter(o =>
    (params.sportIds ?? []).includes(o.value)
  );

  // Badge: number of non-default active filters
  const activeCount = [
    params.friendsOnly,
    (params.sportIds ?? []).length > 0,
  ].filter(Boolean).length;

  return (
    <Accordion
      as={Segment}
      style={{ marginBottom: 12, borderRadius: 10, padding: '8px 14px', borderTop: '3px solid #fbbd08' }}
    >
      <Accordion.Title
        active={open}
        onClick={() => setOpen(!open)}
        style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
      >
        <Icon name='filter' color='yellow' />
        <span>{t('playground.filters')}</span>
        {activeCount > 0 && (
          <Label circular color='yellow' size='mini' style={{ marginLeft: 2 }}>
            {activeCount}
          </Label>
        )}
        <Icon name={open ? 'chevron up' : 'chevron down'} style={{ marginLeft: 'auto' }} />
      </Accordion.Title>

      <Accordion.Content active={open}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, paddingTop: 10 }}>

          {/* ── Radius ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: '0.85em', display: 'block', marginBottom: 4 }}>
              <Icon name='location arrow' color='teal' size='small' />
              {t('playground.radius')}
            </label>
            <Dropdown
              selection
              compact
              options={radiusOptions}
              value={params.radiusKm}
              onChange={(_, d) => onParamChange({ radiusKm: d.value as number })}
            />
          </div>

          {/* ── Time window ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: '0.85em', display: 'block', marginBottom: 4 }}>
              <Icon name='clock outline' color='yellow' size='small' />
              {t('playground.urgency.timeWindow')}
            </label>
            <Dropdown
              selection
              compact
              options={windowOptions}
              value={params.timeWindowHours}
              onChange={(_, d) => onParamChange({ timeWindowHours: d.value as number })}
            />
          </div>

          {/* ── Sport multi-select ── */}
          <div style={{ minWidth: 220, flex: 1 }}>
            <label style={{ fontWeight: 600, fontSize: '0.85em', display: 'block', marginBottom: 4 }}>
              <Icon name='soccer' color='orange' size='small' />
              {t('gameFilters.placeholders.selectSport')}
              {(params.sportIds ?? []).length === 0 && (
                <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 6, fontSize: '0.85em' }}>
                  ({t('playground.urgency.allSports')})
                </span>
              )}
            </label>
            <Select
              isMulti
              options={localizedSportOptions}
              value={selectedSportOptions}
              onChange={selected => {
                onParamChange({ sportIds: selected ? selected.map(s => s.value) : [] });
              }}
              placeholder={t('playground.urgency.allSports')}
              isClearable
              isSearchable
              styles={{
                control: base => ({ ...base, minHeight: 32, fontSize: '0.85em' }),
                menu: base => ({ ...base, fontSize: '0.85em', zIndex: 999 }),
                multiValue: base => ({ ...base, backgroundColor: '#fff3cd' }),
                multiValueLabel: base => ({ ...base, color: '#92400e', fontWeight: 600 }),
                multiValueRemove: base => ({
                  ...base,
                  color: '#92400e',
                  ':hover': { backgroundColor: '#f59e0b', color: '#fff' },
                }),
              }}
            />
          </div>

          {/* ── Friends only toggle ── */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              toggle
              label={t('playground.friendsOnly')}
              checked={params.friendsOnly}
              onChange={(_, d) => onParamChange({ friendsOnly: d.checked })}
            />
          </div>

        </div>
      </Accordion.Content>
    </Accordion>
  );
});